/**
 * Image bank — the hero of the Compose generation model. Browse generated
 * takes and cast art, drag tiles onto the spread, refine or remove takes,
 * and add new images through the embedded generate flow (moments).
 * Ported from the design prototype's spread-editor/panels.jsx.
 */
import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import type { ImageSizePreset, Moment, StoryElement } from '@historyjki/shared';
import {
  BANK_GENERATE_COUNTS,
  DEFAULT_BANK_SIZE_PRESET,
  pagesFromBreaks,
  resolveGenerationSpec,
} from '@historyjki/shared';
import { Button } from '../ui/Button';
import { Chip } from '../ui/Chip';
import { Spinner } from '../ui/Spinner';
import type { ComposeStore, BankTile } from '../../hooks/useComposeStage';
import { startBankDrag } from './SpreadCanvas';
import { r2Url } from '../../utils/r2Url';
import { SizePresetPicker, formatApproxUsd } from './SizePresetPicker';
import {
  IconSparkle,
  IconArrowLeft,
  IconCheck,
  IconPlus,
  IconWand,
  IconX,
  IconUpload,
  IconScissors,
} from './icons';

// ── Generate flow: a story moment is pre-selected; describe your own to
//    define a custom moment. Cast consistency is picked right here. ──────
interface GeneratePanelProps {
  store: ComposeStore;
  onClose: () => void;
}

export function GeneratePanel({ store, onClose }: GeneratePanelProps) {
  const {
    project,
    moments,
    bankTakes,
    beats,
    beatBreaks,
    current,
    interiorIndexOf,
    generateFromMoment,
    generateNew,
  } = store;

  const suggestions = moments;
  const hasImgs = (momentId: string) =>
    bankTakes.some((t) => t.bankImage?.moment_id === momentId);
  const storySuggestions = suggestions.filter((m) => m.source !== 'custom');
  const defaultMoment =
    storySuggestions.find((m) => !hasImgs(m.id)) || storySuggestions[0] || null;

  const elements: StoryElement[] = project?.story_elements ?? [];
  const castFor = (m: Moment | null): string[] => {
    if (!m) return [];
    const ids = new Set<string>();
    try {
      for (const id of JSON.parse(m.character_ids ?? '[]')) ids.add(id);
    } catch { /* malformed JSON — skip */ }
    if (m.place_id) ids.add(m.place_id);
    try {
      for (const id of JSON.parse(m.object_ids ?? '[]')) ids.add(id);
    } catch { /* malformed JSON — skip */ }
    return elements.filter((e) => ids.has(e.id)).map((e) => e.id);
  };

  const [selId, setSelId] = useState<string | null>(defaultMoment?.id ?? null);
  const [desc, setDesc] = useState(defaultMoment?.description ?? '');
  const [cast, setCast] = useState<string[]>(() => castFor(defaultMoment));
  const [count, setCount] = useState<number>(BANK_GENERATE_COUNTS[0]);
  const [sizePreset, setSizePreset] = useState<ImageSizePreset>(DEFAULT_BANK_SIZE_PRESET);
  const [submitting, setSubmitting] = useState(false);

  // Upfront cost: the footprint preset picks the model/size tier, so the
  // author sees exactly what this Generate click spends before clicking.
  const spec = resolveGenerationSpec(sizePreset, project?.format_preset ?? null);
  const totalCost = formatApproxUsd(spec.estCostUsd * count);

  // Opening the flow scrolls the suggestion list to the first beat on the
  // open spread, so the story continues from where the author is composing
  // instead of starting back at the top of the manuscript.
  const suggestRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!current || current.kind !== 'spread') return;
    const interiorIdx = interiorIndexOf(current.id);
    if (interiorIdx < 0) return;
    const groups = pagesFromBreaks(beats.length, beatBreaks);
    // Spread N holds pages 2N and 2N+1 of the beat grouping; the target is
    // the first of its beats whose moment is still in the list.
    const beatIdxs = [
      ...(groups[interiorIdx * 2] ?? []),
      ...(groups[interiorIdx * 2 + 1] ?? []),
    ];
    const momentId = beatIdxs
      .map((i) => beats[i]?.moment_id)
      .find((id) => id && storySuggestions.some((m) => m.id === id));
    if (!momentId) return;
    const list = suggestRef.current;
    const row = list?.querySelector<HTMLElement>(
      `[data-moment-id="${CSS.escape(momentId)}"]`,
    );
    if (list && row) {
      list.scrollTop += row.getBoundingClientRect().top - list.getBoundingClientRect().top;
    }
    // Scroll once when the panel opens; later refreshes must not yank the
    // list out from under the author.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selMoment = suggestions.find((m) => m.id === selId) ?? null;
  const toggleCast = (id: string) =>
    setCast((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));

  const pickSuggestion = (m: Moment) => {
    if (selId === m.id) {
      setSelId(null);
      setDesc('');
      setCast([]);
      return;
    }
    setSelId(m.id);
    setDesc(m.description);
    setCast(castFor(m));
  };

  const onDesc = (v: string) => {
    setDesc(v);
    if (selMoment && v !== selMoment.description) setSelId(null);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      if (selId) {
        onClose();
        await generateFromMoment(selId, { count, consistencyElementIds: cast, sizePreset });
      } else {
        if (!desc.trim()) return;
        onClose();
        await generateNew({ description: desc.trim(), consistencyElementIds: cast, count, sizePreset });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="genflow" data-testid="generate-panel">
      <button type="button" className="genflow__back" onClick={onClose}>
        <IconArrowLeft /> Bank
      </button>
      <div className="genflow__title">Generate a new image</div>

      <div className="genflow__suggest-head">Moment from your story</div>
      <div className="genflow__suggest" ref={suggestRef}>
        {storySuggestions.map((m) => {
          const on = selId === m.id;
          const drawn = hasImgs(m.id);
          return (
            <button
              key={m.id}
              type="button"
              className={`suggest-row ${on ? 'suggest-row--on' : ''}`}
              data-moment-id={m.id}
              onClick={() => pickSuggestion(m)}
            >
              <span className="suggest-row__sw" />
              <span className="suggest-row__body">
                <span className="suggest-row__title">{m.title}</span>
                <span className="suggest-row__meta">
                  {m.source}
                  {drawn ? ' · drawn' : ''}
                </span>
              </span>
              {on ? <IconCheck /> : <IconPlus />}
            </button>
          );
        })}
        {storySuggestions.length === 0 && (
          <div className="bank-empty">No story moments yet — describe your own below.</div>
        )}
      </div>

      <label className="genflow__label">
        {selMoment ? 'Moment description' : 'Describe your own moment'}
      </label>
      <textarea
        className="genflow__area"
        value={desc}
        onChange={(e) => onDesc(e.target.value)}
        rows={3}
        placeholder="A small silver bear with snow on his shoulders, meeting a girl in a red scarf among the apple trees at dusk…"
      />
      <p className="genflow__note">Edit the text to define your own moment instead.</p>

      <label className="genflow__label">
        Keep consistent with <span className="genflow__opt">cast & references</span>
      </label>
      <div className="genflow__cast">
        {elements.map((el) => (
          <button
            key={el.id}
            type="button"
            className={`castchip ${cast.includes(el.id) ? 'castchip--on' : ''}`}
            onClick={() => toggleCast(el.id)}
            title={el.kind}
          >
            <span className="castchip__sw" />
            {el.name}
            {cast.includes(el.id) && <IconCheck />}
          </button>
        ))}
        {elements.length === 0 && (
          <p className="genflow__note">No cast yet — define characters, places and objects on Define.</p>
        )}
      </div>

      <label className="genflow__label">
        Size on the page <span className="genflow__opt">sets print sharpness & cost</span>
      </label>
      <SizePresetPicker
        formatPreset={project?.format_preset ?? null}
        value={sizePreset}
        onChange={setSizePreset}
      />

      <div className="genflow__foot">
        <div className="genflow__count">
          {BANK_GENERATE_COUNTS.map((n) => (
            <button
              key={n}
              type="button"
              className={count === n ? 'is-on' : ''}
              onClick={() => setCount(n)}
            >
              {n}
            </button>
          ))}
          <span>images</span>
        </div>
        <Button
          variant="accent"
          icon={<IconSparkle />}
          onClick={() => void submit()}
          disabled={submitting || (!selId && !desc.trim())}
          title={`Estimated cost for ${count} image${count === 1 ? '' : 's'} at this size`}
        >
          Generate · {totalCost}
        </Button>
      </div>
    </div>
  );
}

// ── Image bank (hero) — browse, drag onto spread, or add a new image ────
interface BankPanelProps {
  store: ComposeStore;
  onEditCastElement: (elementId: string) => void;
  onRefineBankImage: (bankImageId: string, r2Key: string, caption: string) => void;
}

export function BankPanel({ store, onEditCastElement, onRefineBankImage }: BankPanelProps) {
  const {
    bankTakes,
    castTiles,
    pendingGenerations,
    addImageAt,
    deleteBankImage,
    uploadBankImage,
    cutoutBankImageId,
    cutoutBankImage,
  } = store;
  const [filter, setFilter] = useState<'ai' | 'cast' | 'all'>('ai');
  const [view, setView] = useState<'grid' | 'gen'>('grid');
  const fileRef = useRef<HTMLInputElement | null>(null);

  if (view === 'gen') return <GeneratePanel store={store} onClose={() => setView('grid')} />;

  const items: BankTile[] =
    filter === 'cast' ? castTiles : filter === 'all' ? [...bankTakes, ...castTiles] : bankTakes;
  const shimmer =
    filter === 'cast'
      ? []
      : pendingGenerations.flatMap((p) =>
          Array.from({ length: p.count }, (_, i) => `${p.key}-${i}`),
        );

  // Cast tiles open the cast editor on click; a real drag (>4px) places
  // the tile's reference art instead.
  const onTilePointerDown = (e: ReactPointerEvent, tile: BankTile) => {
    if (tile.kind === 'take') {
      startBankDrag(e, tile, addImageAt);
      return;
    }
    const sx = e.clientX;
    const sy = e.clientY;
    let dragging = false;
    const move = (ev: PointerEvent) => {
      if (!dragging && Math.hypot(ev.clientX - sx, ev.clientY - sy) > 4) {
        dragging = true;
        cleanup();
        if (tile.r2_key) {
          startBankDrag(e, tile, addImageAt);
        }
      }
    };
    const up = () => {
      cleanup();
      if (!dragging && tile.element) onEditCastElement(tile.element.id);
    };
    const cleanup = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  return (
    <div className="panel" data-testid="bank-panel">
      <button type="button" className="newimg" onClick={() => setView('gen')}>
        <IconSparkle /> New image…
      </button>

      <div className="bank-filters">
        <Chip selected={filter === 'ai'} onClick={() => setFilter('ai')}>
          Generated · {bankTakes.length}
        </Chip>
        <Chip selected={filter === 'cast'} onClick={() => setFilter('cast')}>
          Cast · {castTiles.length}
        </Chip>
        <Chip selected={filter === 'all'} onClick={() => setFilter('all')}>
          All
        </Chip>
        <label className="bank-upload" title="Add your own image to the bank">
          <input
            ref={fileRef}
            type="file"
            hidden
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void uploadBankImage(file);
              e.target.value = '';
            }}
          />
          <IconUpload /> Upload
        </label>
      </div>
      <p className="panel__hint">
        Drag any tile onto the spread to place it — resize and layer freely. Click cast art to
        edit its visual definition and reference photos.
      </p>

      <div className="bank">
        {shimmer.map((k) => (
          <div key={k} className="bank-tile bank-tile--gen" data-testid="bank-shimmer">
            <div className="bank-tile__shim" />
            <span className="bank-tile__cap">generating…</span>
          </div>
        ))}
        {items.map((tile) => (
          <div
            key={tile.key}
            className="bank-tile"
            data-testid={tile.kind === 'take' ? 'bank-take' : 'bank-cast'}
            title={
              tile.kind === 'take'
                ? `Drag to place · ${tile.caption}`
                : `${tile.caption} — click to edit, drag to place`
            }
            onPointerDown={(e) => onTilePointerDown(e, tile)}
          >
            {tile.r2_key ? (
              <img src={r2Url(tile.r2_key)} alt={tile.caption} draggable={false} />
            ) : (
              <div className="bank-tile__ph" />
            )}
            {tile.kind === 'cast' && <span className="bank-tile__kind">{tile.castKind}</span>}
            {tile.kind === 'take' && tile.stale && (
              <span className="bank-tile__stale" title={tile.stale_reason ?? 'Cast updated since this was drawn'}>
                stale
              </span>
            )}
            <span className="bank-tile__cap">{tile.caption}</span>
            {tile.kind === 'take' && tile.bankImage && (
              <span className="bank-tile__actions" onPointerDown={(e) => e.stopPropagation()}>
                <button
                  type="button"
                  className="bank-tile__action"
                  title="Refine this image"
                  aria-label="Refine this image"
                  onClick={() =>
                    onRefineBankImage(tile.bankImage!.id, tile.bankImage!.r2_key, tile.caption)
                  }
                >
                  <IconWand />
                  <span>Refine</span>
                </button>
                <button
                  type="button"
                  className="bank-tile__action"
                  title="Cut out the subject (remove the background)"
                  aria-label="Cut out the subject (remove the background)"
                  disabled={cutoutBankImageId !== null}
                  onClick={() =>
                    void cutoutBankImage(tile.bankImage!.id, tile.bankImage!.r2_key, tile.caption)
                  }
                >
                  {cutoutBankImageId === tile.bankImage.id ? <Spinner size="sm" /> : <IconScissors />}
                  <span>Cut out</span>
                </button>
                <button
                  type="button"
                  className="bank-tile__action bank-tile__action--danger"
                  title="Remove from bank"
                  aria-label="Remove from bank"
                  onClick={() => void deleteBankImage(tile.bankImage!.id)}
                >
                  <IconX />
                  <span>Remove</span>
                </button>
              </span>
            )}
          </div>
        ))}
        {items.length === 0 && shimmer.length === 0 && (
          <div className="bank-empty">Nothing here yet — add a new image.</div>
        )}
      </div>
    </div>
  );
}
